import type { StoryVideo } from '../types.ts/story';

/** Minimal structural shape needed to walk a video's aspect-ratio variant graph. */
export type ArVariantLike = {
  id?: string;
  aspectRatio?: string;
  videoFilePrimary?: StoryVideo;
  associatedVideos?: ArVariantLike[] | null;
  parentVideo?: ArVariantLike | null;
};

/**
 * Resolve the variant matching a given aspect ratio from the currently loaded
 * video and its variant graph (itself, its parent, and the parent's associated
 * videos). The published render file (`videoFilePrimary`) can live on any of
 * these — not just `currentVideo` — so the status badge and the download button
 * must both resolve through this graph to stay in sync.
 *
 * Among variants matching the aspect ratio, a *published* one (carrying a
 * `videoFilePrimary`) wins over an unpublished draft — the published render can
 * live on a sibling/parent while `currentVideo` is an unpublished draft of the
 * same AR. Returns `undefined` when no variant for this AR exists.
 */
export function resolveAspectRatioVariant(
  aspectRatio: string,
  currentVideo: ArVariantLike | null | undefined,
): ArVariantLike | undefined {
  if (!currentVideo) return undefined;
  const parentVideo = currentVideo.parentVideo || currentVideo;
  const candidates = [
    currentVideo,
    parentVideo,
    ...(parentVideo.associatedVideos || []),
  ].filter((v): v is ArVariantLike => v?.aspectRatio === aspectRatio);
  return candidates.find(v => v.videoFilePrimary) ?? candidates[0];
}
